import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["block", "component", "children", "className", "disabled", "loading", "loadingMessage", "primary", "tertiary", "dangerouslyForceFocusStyling", "dangerouslyForceActiveStyling", "dangerouslyForceHoverStyling"];
import React from 'react';
import classNames from 'classnames';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { Accent } from '../Accent/Accent';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ButtonSpinnerAnimation } from './ButtonSpinnerAnimation';
import styles from './Button.module.less';

var NOOP = function NOOP() {};

var inferComponent = function inferComponent(_ref) {
  var href = _ref.href;
  return href ? 'a' : 'button';
};

var preventDefaultAndStopPropagation = function preventDefaultAndStopPropagation(e) {
  e.preventDefault();
  e.stopPropagation();
};

var getDisabledProps = function getDisabledProps(Component, disabled) {
  if (!disabled) {
    return {};
  }

  if (Component === 'a') {
    return {
      tabIndex: -1,
      'aria-disabled': true,
      onMouseDown: preventDefaultAndStopPropagation,
      onClick: preventDefaultAndStopPropagation
    };
  }

  return {
    disabled: true
  };
};

var getTypeProps = function getTypeProps(Component) {
  // Should use type=button when using a <button> component, but not needed
  // when using <a>. This can be overridden by passing a `type` prop.
  if (Component === 'button') {
    return {
      type: 'button'
    };
  }

  return {};
};
/**
 * Buttons indicate to a user that an important action is available (e.g., start a flow, proceed to next steps, send your info, save for later). Button hierarchy helps communicate the importance of one action vs. another.
 */


export var Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;

  var block = props.block,
      component = props.component,
      children = props.children,
      className = props.className,
      disabled = props.disabled,
      loading = props.loading,
      loadingMessage = props.loadingMessage,
      primary = props.primary,
      tertiary = props.tertiary,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceActiveStyling = props.dangerouslyForceActiveStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      otherProps = _objectWithoutProperties(props, _excluded);

  var Component = component || inferComponent(otherProps);
  useDeprecatedClassNameStylePropsWarning('Button', {
    className: className,
    style: otherProps.style
  });
  return /*#__PURE__*/React.createElement(Component // bind a noop ontouchstart handler
  // so that ios will style :active states
  , _extends({
    onTouchStart: NOOP,
    className: classNames(className, styles.button, (_classNames = {}, _defineProperty(_classNames, styles['link-button'], Component === 'a'), _defineProperty(_classNames, styles.loading, loading), _defineProperty(_classNames, styles.primary, !disabled && primary), _defineProperty(_classNames, styles.secondary, !disabled && !primary && !tertiary), _defineProperty(_classNames, styles.tertiary, !disabled && !primary && tertiary), _defineProperty(_classNames, styles['primary-disabled'], disabled && primary), _defineProperty(_classNames, styles['secondary-disabled'], disabled && !primary && !tertiary), _defineProperty(_classNames, styles['tertiary-disabled'], disabled && !primary && tertiary), _defineProperty(_classNames, styles.block, block), _defineProperty(_classNames, styles['force-focus'], dangerouslyForceFocusStyling), _defineProperty(_classNames, styles['force-hover'], dangerouslyForceHoverStyling), _defineProperty(_classNames, styles['force-active'], dangerouslyForceActiveStyling), _classNames)),
    "data-currency": "Button",
    ref: ref
  }, getTypeProps(Component), otherProps, getDisabledProps(Component, !!disabled)), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement(Accent, {
    inline: true
  }, children)), /*#__PURE__*/React.createElement("div", {
    className: styles['loading-container']
  }, /*#__PURE__*/React.createElement(ButtonSpinnerAnimation, {
    show: !!loading
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    color: primary ? 'green-light' : 'green',
    loadingMessage: loadingMessage // https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA22
    // This component uses the "status" role to notify screen readers that
    // the page has updated with information about the application's status.
    // This is the same as using aria-live="polite", which will not
    // interrupt the screen reader's current task.
    ,
    role: "status",
    size: 24
  }))));
});
Button.displayName = 'Button';